<script setup lang="ts">
import { reactive, ref, watch, nextTick, onUnmounted, computed, onMounted } from 'vue'
import type { Rule } from 'ant-design-vue/es/form'
import {
  strongPasswordSchema,
  cnMobileSchema,
  PHONE_LIMITS,
  smsOtpSchema,
  personNameSchema,
  PERSON_NAME_LIMITS,
  requiredText,
} from '@/schemas/fields'
import { fieldRule, fieldRuleWhen } from '@/utils/formRules'

const profileConfirmPasswordSchema = requiredText('登录密码', 32)
const oldPasswordSchema = requiredText('原密码', 32)
import {
  BellOutlined,
  UserOutlined,
  DownOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  LockOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons-vue'
import {
  fetchNotificationList,
  fetchNotificationDetail,
  INBOX_PREVIEW_LIMIT,
  notificationLevelLabel,
  notificationLevelTagColor,
  markAllMessagesRead,
  type NotificationItem,
} from '@/api'
import {
  fetchJitAuthRandom,
  submitJitP1Auth,
  fetchJitAuthBindInfo,
  bindJitAuthToAccount,
  formatJitAuthBindTime,
  pickJitCertSn,
  type JitAuthBindInfo,
  type JitP1AuthResult,
} from '@/api/jitAuth'
import { loadJitCertList, signJitP1WithPin, type JitCertOption } from '@/composables/useJitCertAuth'
import {
  buildJitCertDisplayEntries,
  buildJitExtraCertDisplayEntries,
  buildJitUmsDisplayEntries,
  formatJitCertSubject,
  formatJitAuthFieldLabel,
  type JitAuthDisplayEntry,
} from '@/utils/jitAuthDisplay'
import {
  updateUserInfo,
  updateAvatar,
  updatePasswordByOldPassword,
  resetPasswordByPhone,
  fetchUserCenterPicCaptcha,
  findPasswordGetPhoneValidCode,
} from '@/api/userCenter'
import { useAuthStore } from '@/stores/auth'
import { useSysBrandingStore } from '@/stores/sysBranding'
import { useUserProfileStore } from '@/stores/userProfile'
import { useMessageNoticeStore } from '@/stores/messageNotice'
import { useMessage } from '@/composables/useMessage'
import { useRouter } from 'vue-router'
import { usePermissionStore } from '@/stores/permission'
import {
  hasLargeScreenOrgPerm,
  hasLargeScreenRegionPerm,
  resolveDashboardScreenRouteName,
} from '@/views/dashboard/screenMode'
import HeaderManualDownload from './HeaderManualDownload.vue'
import {
  formatPasswordDateTime,
  hasPasswordExpireRemindedThisLogin,
  markPasswordExpireRemindedThisLogin,
  shouldShowPasswordExpireRemind,
  PASSWORD_VALID_DAYS,
  PASSWORD_REMIND_LAST_DAYS,
} from '@/utils/passwordExpire'

const router = useRouter()
const permissionStore = usePermissionStore()
const authStore = useAuthStore()
const sysBranding = useSysBrandingStore()
const userProfileStore = useUserProfileStore()
const messageNoticeStore = useMessageNoticeStore()
const { msg } = useMessage()
const notificationModalOpen = ref(false)
const notificationsUnread = ref<NotificationItem[]>([])
const notificationsRead = ref<NotificationItem[]>([])
const notificationTabKey = ref<'unread' | 'read'>('unread')
const notificationLoading = ref(false)
const notificationDetailOpen = ref(false)
const notificationDetailItem = ref<NotificationItem | null>(null)
const unreadNotificationCount = computed(() => messageNoticeStore.unreadCount)
const readNotificationCount = computed(() => messageNoticeStore.readCount)

const modalNotifications = computed(() =>
  notificationTabKey.value === 'read' ? notificationsRead.value : notificationsUnread.value,
)

const inboxPreviewHint = computed(() => {
  const total =
    notificationTabKey.value === 'read'
      ? readNotificationCount.value
      : unreadNotificationCount.value
  const shown = modalNotifications.value.length
  if (total > shown && shown > 0) {
    return `共 ${total} 条，此处仅显示最近 ${INBOX_PREVIEW_LIMIT} 条`
  }
  return ''
})

const profileModalOpen = ref(false)
const profileEditModalOpen = ref(false)
const profileSubmitting = ref(false)
const avatarUploading = ref(false)
const profileFormRef = ref()
const profileForm = reactive({
  name: '',
  phone: '',
  password: '',
  avatarUrl: '' as string | null,
})

const profileRules: Record<string, Rule[]> = {
  name: [fieldRule(personNameSchema)],
  phone: [fieldRule(cnMobileSchema)],
  password: [fieldRule(profileConfirmPasswordSchema)],
}

const authBindInfo = ref<JitAuthBindInfo | null>(null)
const authBindInfoLoading = ref(false)
const authBindModalOpen = ref(false)
const authBindSubmitting = ref(false)
const authBindBinding = ref(false)
const authBindCertLoading = ref(false)
const authBindCertOptions = ref<JitCertOption[]>([])
const authBindForm = reactive({
  certSn: '',
  pin: '',
})
const authBindResult = ref<JitP1AuthResult | null>(null)
/** 0=证书认证 1=认证结果（同一弹窗内步骤切换） */
const authBindStepIndex = ref(0)
const authBindResultMode = ref<'confirm' | 'view'>('confirm')
const authBindModalBodyRef = ref<HTMLElement | null>(null)

const authBindStepItems = [
  { title: '证书认证', description: '选择证书并验证 PIN' },
  { title: '认证结果', description: '核对信息并绑定账号' },
]

const authBindStatusText = computed(() => {
  if (authBindInfoLoading.value) return '加载中…'
  if (!authBindInfo.value?.bound) return '未绑定'
  const subject =
    authBindInfo.value.certAttributes?.['X509Certificate.SubjectDN'] ||
    authBindInfo.value.certAttributes?.['_saml_pki_cert_subject'] ||
    authBindInfo.value.certDn
  return subject || authBindInfo.value.certSn || '已绑定'
})

const profileAuthBindShortText = computed(() => {
  if (authBindInfoLoading.value) return '加载中…'
  if (!authBindInfo.value?.bound) return '尚未绑定数字证书'
  const full = authBindStatusText.value
  if (full === '已绑定' || full === '未绑定') return full
  return formatJitCertSubject(full)
})

const profileAuthBindFullText = computed(() => {
  if (!authBindInfo.value?.bound) return ''
  return authBindStatusText.value
})

const authBindDisplayCertAttributes = computed(() =>
  authBindStepIndex.value === 1 && authBindResultMode.value === 'view'
    ? authBindInfo.value?.certAttributes
    : authBindResult.value?.certAttributes,
)

const authBindDisplayUmsAttributes = computed(() =>
  authBindStepIndex.value === 1 && authBindResultMode.value === 'view'
    ? authBindInfo.value?.umsAttributes
    : authBindResult.value?.umsAttributes,
)

const authBindCertEntries = computed<JitAuthDisplayEntry[]>(() =>
  buildJitCertDisplayEntries(authBindDisplayCertAttributes.value).map((item) => ({
    ...item,
    label: formatJitAuthFieldLabel(item.key),
  })),
)

const authBindExtraCertEntries = computed(() =>
  buildJitExtraCertDisplayEntries(authBindDisplayCertAttributes.value),
)

const authBindUmsEntries = computed<JitAuthDisplayEntry[]>(() =>
  buildJitUmsDisplayEntries(authBindDisplayUmsAttributes.value),
)

const authBindHasCertInfo = computed(() => authBindCertEntries.value.length > 0)
const authBindHasUmsInfo = computed(() => authBindUmsEntries.value.length > 0)
const authBindHasExtraCertInfo = computed(() => authBindExtraCertEntries.value.length > 0)

const authBindCertSummary = computed(() => {
  const result = authBindResult.value
  const attrs = authBindDisplayCertAttributes.value ?? {}
  const subjectRaw =
    result?.certDn || attrs['X509Certificate.SubjectDN'] || attrs['_saml_pki_cert_subject'] || ''
  return {
    subject: subjectRaw ? formatJitCertSubject(subjectRaw) : '—',
    subjectFull: subjectRaw || '—',
    serial: pickJitCertSn(result, attrs['X509Certificate.SerialNumber']) || '—',
    notBefore: attrs['X509Certificate.NotBefore'] || '',
    notAfter: attrs['X509Certificate.NotAfter'] || '',
    issuer: attrs['X509Certificate.IssuerDN'] || '',
  }
})

const authBindResultCollapseKeys = ref<string[]>([])

const displayName = computed(() => {
  const p = userProfileStore.profile
  if (!p) return userProfileStore.loading ? '加载中…' : '用户'
  return p.realName?.trim() || p.username || '用户'
})

const changePasswordModalOpen = ref(false)
const changePasswordSubmitting = ref(false)
const changePasswordFormRef = ref()
const changePasswordForm = reactive({
  oldPassword: '',
  smsCode: '',
  newPassword: '',
  confirmPassword: '',
})

const changePasswordHasPhone = computed(() => {
  const phone = (userProfileStore.profile?.phone ?? '').trim()
  return Boolean(phone && phoneRegex.test(phone))
})

const passwordExpireRemindOpen = ref(false)

const passwordExpireView = computed(() => {
  const p = userProfileStore.profile
  const expired = Boolean(p?.passwordExpired)
  const remain = p?.passwordRemainDays
  const expireText = formatPasswordDateTime(p?.passwordExpireTime)
  const fromCreateTime = !p?.passwordUpdateTime && Boolean(p?.createTime)
  const updateText = formatPasswordDateTime(p?.passwordUpdateTime || p?.createTime)
  const updateLabel = fromCreateTime ? '账号创建' : '上次改密'
  let tone: 'ok' | 'warn' | 'danger' = 'ok'
  let statusText = `密码有效期 ${PASSWORD_VALID_DAYS} 天`
  if (expired) {
    tone = 'danger'
    statusText = '密码已过期，请尽快修改'
  } else if (!p?.passwordExpireTime) {
    tone = 'danger'
    statusText = '无法计算密码有效期，请尽快修改'
  } else if (typeof remain === 'number' && remain <= 10) {
    tone = 'warn'
    statusText = `密码即将过期，剩余 ${remain} 天`
  } else if (typeof remain === 'number') {
    statusText = `密码有效期内，剩余 ${remain} 天`
  }
  return { expired, remain, expireText, updateText, updateLabel, tone, statusText }
})

function maybeOpenPasswordExpireRemind() {
  const p = userProfileStore.profile
  const token = authStore.token
  if (!authStore.isLoggedIn || !p?.id || !token) return
  if (
    !shouldShowPasswordExpireRemind({
      expired: p.passwordExpired,
      remainDays: p.passwordRemainDays,
    })
  ) {
    return
  }
  if (hasPasswordExpireRemindedThisLogin(token)) return
  passwordExpireRemindOpen.value = true
}

function dismissPasswordExpireRemind() {
  const token = authStore.token
  if (token) markPasswordExpireRemindedThisLogin(token)
  passwordExpireRemindOpen.value = false
}

function goChangePasswordFromRemind() {
  dismissPasswordExpireRemind()
  changePasswordModalOpen.value = true
}

const phoneRegex = /^1\d{10}$/
const smsCountdown = ref(0)
let smsCountdownTimer: ReturnType<typeof setInterval> | null = null
const phoneValidCodeReqNo = ref('')

const passwordRuleChecks = computed(() => {
  const v = String(changePasswordForm.newPassword ?? '')
  return [
    { key: 'len', label: '8–32 位', ok: v.length >= 8 && v.length <= 32 },
    { key: 'num', label: '数字', ok: /[0-9]/.test(v) },
    { key: 'upper', label: '大写字母', ok: /[A-Z]/.test(v) },
    { key: 'lower', label: '小写字母', ok: /[a-z]/.test(v) },
    { key: 'special', label: '特殊字符', ok: /[^A-Za-z0-9]/.test(v) },
  ]
})

const confirmPasswordMatched = computed(() => {
  const a = String(changePasswordForm.newPassword ?? '')
  const b = String(changePasswordForm.confirmPassword ?? '')
  return Boolean(a) && a === b
})

/** GET /auth/b/getPicCaptcha 解包后的 data（与登录页一致） */
interface PicCaptchaPayload {
  validCodeBase64?: string
  validCodeReqNo?: string
}

function toCaptchaImgSrc(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  if (s.startsWith('data:')) return s
  if (s.startsWith('http://') || s.startsWith('https://')) return s
  return `data:image/png;base64,${s}`
}

function parseCaptchaPayload(data: unknown): { imgBase64: string; reqNo: string } | null {
  if (!data || typeof data !== 'object') return null
  const o = data as PicCaptchaPayload
  const imgStr = typeof o.validCodeBase64 === 'string' ? o.validCodeBase64.trim() : ''
  const reqStr =
    o.validCodeReqNo != null && String(o.validCodeReqNo).trim() !== ''
      ? String(o.validCodeReqNo).trim()
      : ''
  if (!imgStr || !reqStr) return null
  return { imgBase64: imgStr, reqNo: reqStr }
}

/** 修改密码：发送短信前图形验证（与 login/index.vue 短信登录流程一致） */
const changePasswordSmsPicModalOpen = ref(false)
const changePasswordSmsPicCaptchaInput = ref('')
const changePasswordSmsPicCaptchaImgSrc = ref('')
const changePasswordSmsPicValidCodeReqNo = ref('')
const changePasswordSmsPicCaptchaLoading = ref(false)
const changePasswordSmsSendSubmitting = ref(false)

async function fetchChangePasswordSmsModalPicCaptcha() {
  changePasswordSmsPicCaptchaLoading.value = true
  changePasswordSmsPicCaptchaInput.value = ''
  try {
    // 须与 findPassword* 同一缓存前缀 user-validCode（勿用 /auth/b/getPicCaptcha）
    const data = await fetchUserCenterPicCaptcha()
    const parsed = parseCaptchaPayload(data)
    if (!parsed) {
      msg.error('验证码数据格式异常')
      changePasswordSmsPicCaptchaImgSrc.value = ''
      changePasswordSmsPicValidCodeReqNo.value = ''
      return
    }
    changePasswordSmsPicCaptchaImgSrc.value = toCaptchaImgSrc(parsed.imgBase64)
    changePasswordSmsPicValidCodeReqNo.value = parsed.reqNo
  } catch {
    changePasswordSmsPicCaptchaImgSrc.value = ''
    changePasswordSmsPicValidCodeReqNo.value = ''
  } finally {
    changePasswordSmsPicCaptchaLoading.value = false
  }
}

function refreshChangePasswordSmsModalPicCaptcha() {
  void fetchChangePasswordSmsModalPicCaptcha()
}

function closeChangePasswordSmsPicModal() {
  changePasswordSmsPicModalOpen.value = false
}

/** 兼容接口直接返回或嵌套在 data 下的 validCodeReqNo（与登录页短信逻辑一致） */
function parseValidCodeReqNoFromSmsResponse(data: unknown): string {
  if (data == null) return ''
  if (typeof data === 'string' || typeof data === 'number') {
    return String(data).trim()
  }
  if (typeof data !== 'object') return ''
  const root = data as Record<string, unknown>
  if ('validCodeReqNo' in root) {
    return String(root.validCodeReqNo ?? '').trim()
  }
  const inner = root.data
  if (inner != null && typeof inner === 'object' && 'validCodeReqNo' in inner) {
    return String((inner as Record<string, unknown>).validCodeReqNo ?? '').trim()
  }
  return ''
}

async function confirmSendChangePasswordSmsCode() {
  if (changePasswordSmsSendSubmitting.value) return Promise.reject()
  const phone = (userProfileStore.profile?.phone ?? '').trim()
  if (!phone) {
    msg.warning('当前账号未绑定手机号，无法使用短信验证')
    return Promise.reject()
  }
  const pic = changePasswordSmsPicCaptchaInput.value.trim()
  if (!pic) {
    msg.warning('请输入图中验证码')
    return Promise.reject()
  }
  if (!changePasswordSmsPicValidCodeReqNo.value) {
    msg.warning('请先获取图形验证码')
    await fetchChangePasswordSmsModalPicCaptcha()
    return Promise.reject()
  }
  changePasswordSmsSendSubmitting.value = true
  try {
    // 与 resetPasswordByPhone(/sys/userCenter/findPasswordByPhone) 共用 user-validCode
    const data = await findPasswordGetPhoneValidCode({
      phone,
      userMode: 100,
      validCode: pic,
      validCodeReqNo: changePasswordSmsPicValidCodeReqNo.value,
    })
    const reqNo = parseValidCodeReqNoFromSmsResponse(data)
    if (!reqNo) {
      msg.error('未获取到验证码请求号，请重试')
      return Promise.reject()
    }
    phoneValidCodeReqNo.value = reqNo
    msg.success('验证码已发送')
    startSmsCountdown()
    changePasswordSmsPicModalOpen.value = false
  } catch (e) {
    await fetchChangePasswordSmsModalPicCaptcha()
    return Promise.reject(e)
  } finally {
    changePasswordSmsSendSubmitting.value = false
  }
}

const changePasswordRules = computed<Record<string, Rule[]>>(() => ({
  oldPassword: [
    fieldRuleWhen(
      oldPasswordSchema,
      () => !changePasswordHasPhone.value,
      ['change', 'blur'],
      !changePasswordHasPhone.value,
    ),
  ],
  smsCode: [
    fieldRuleWhen(
      smsOtpSchema,
      () => changePasswordHasPhone.value,
      ['change', 'blur'],
      changePasswordHasPhone.value,
    ),
  ],
  newPassword: [fieldRule(strongPasswordSchema, ['change', 'blur'], true)],
  confirmPassword: [
    fieldRule(profileConfirmPasswordSchema, ['change', 'blur'], true),
    {
      validator: async (_rule, value: string) => {
        if (!value) return Promise.resolve()
        if (value !== changePasswordForm.newPassword) {
          return Promise.reject(new Error('两次输入的密码不一致'))
        }
        return Promise.resolve()
      },
      trigger: ['change', 'blur'],
    },
  ],
}))

function resetChangePasswordForm() {
  changePasswordForm.oldPassword = ''
  changePasswordForm.smsCode = ''
  changePasswordForm.newPassword = ''
  changePasswordForm.confirmPassword = ''
  phoneValidCodeReqNo.value = ''
  smsCountdown.value = 0
  stopSmsCountdown()
}

function startSmsCountdown() {
  if (smsCountdownTimer) return
  smsCountdown.value = 60
  smsCountdownTimer = setInterval(() => {
    smsCountdown.value -= 1
    if (smsCountdown.value <= 0 && smsCountdownTimer) {
      clearInterval(smsCountdownTimer)
      smsCountdownTimer = null
    }
  }, 1000)
}

function stopSmsCountdown() {
  if (smsCountdownTimer) {
    clearInterval(smsCountdownTimer)
    smsCountdownTimer = null
  }
}

onUnmounted(() => {
  if (smsCountdownTimer) clearInterval(smsCountdownTimer)
})

watch(changePasswordModalOpen, async (open) => {
  if (!open) {
    changePasswordSmsPicModalOpen.value = false
    return
  }
  resetChangePasswordForm()
  await nextTick()
  changePasswordFormRef.value?.clearValidate()
  if (!userProfileStore.profile) {
    await userProfileStore.load()
  }
  if (changePasswordHasPhone.value) {
    void fetchChangePasswordSmsModalPicCaptcha()
  }
})

onMounted(() => {
  // 路由守卫里 loadCodes 已用同源接口写入 profile，避免重复请求 getSimpleLoginUser
  if (authStore.isLoggedIn && !userProfileStore.profile) {
    void userProfileStore.load()
  }
  if (authStore.isLoggedIn) {
    void loadAuthBindInfo()
  }
  // 初始化只拉一次用于角标提示
  void loadNotifications()
  maybeOpenPasswordExpireRemind()

  // 右侧“消息提醒”面板刷新/全部已读后，通过全局事件通知 Header 更新角标
  const onMessageChanged = () => void messageNoticeStore.refreshCounts()
  window.addEventListener('sys:index-message-changed', onMessageChanged as EventListener)
  onUnmounted(() => {
    window.removeEventListener('sys:index-message-changed', onMessageChanged as EventListener)
  })
})

watch(
  () => userProfileStore.profile,
  () => {
    maybeOpenPasswordExpireRemind()
  },
)

watch(
  () => authStore.isLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      if (!userProfileStore.profile) {
        void userProfileStore.load()
      }
      void loadAuthBindInfo()
      void loadNotifications()
    } else {
      userProfileStore.clear()
      authBindInfo.value = null
      notificationsUnread.value = []
      notificationsRead.value = []
      messageNoticeStore.clear()
      passwordExpireRemindOpen.value = false
    }
  },
)

async function loadAuthBindInfo() {
  if (!authStore.isLoggedIn) {
    authBindInfo.value = null
    return
  }
  authBindInfoLoading.value = true
  try {
    authBindInfo.value = await fetchJitAuthBindInfo()
  } catch {
    authBindInfo.value = { bound: false }
  } finally {
    authBindInfoLoading.value = false
  }
}

async function openProfileModal() {
  profileModalOpen.value = true
  // 进入页已在 onMounted / 登录态监听中拉取过用户信息，有缓存则不再请求
  if (!userProfileStore.profile) {
    await userProfileStore.load()
  }
  void loadAuthBindInfo()
}

function onProfileCancel() {
  profileModalOpen.value = false
}

async function openAuthBindModal() {
  authBindModalOpen.value = true
  authBindStepIndex.value = 0
  authBindResultMode.value = 'confirm'
  authBindResult.value = null
  authBindForm.pin = ''
  authBindCertLoading.value = true
  try {
    const options = await loadJitCertList()
    authBindCertOptions.value = options
    authBindForm.certSn = options[0]?.sn ?? ''
    if (!options.length) {
      msg.warning('未检测到可用数字证书，请确认已插入 USB Key')
    }
  } catch (e: unknown) {
    authBindCertOptions.value = []
    authBindForm.certSn = ''
    const err = e as { message?: string }
    msg.error(err?.message || '获取证书列表失败')
  } finally {
    authBindCertLoading.value = false
  }
}

function openAuthBindResultView() {
  const info = authBindInfo.value
  if (!info?.bound) return
  authBindResult.value = {
    success: true,
    certDn: info.certDn,
    certSn: info.certSn,
    certAttributes: info.certAttributes,
    umsAttributes: info.umsAttributes,
    pmsAttributes: info.pmsAttributes,
    customAttributes: info.customAttributes,
  }
  authBindStepIndex.value = 1
  authBindResultMode.value = 'view'
  authBindModalOpen.value = true
}

function resetAuthBindModalState() {
  authBindStepIndex.value = 0
  authBindResultMode.value = 'confirm'
  authBindResult.value = null
  authBindForm.pin = ''
}

function closeAuthBindResult() {
  authBindModalOpen.value = false
  resetAuthBindModalState()
}

function onAuthBindCancel() {
  authBindModalOpen.value = false
  resetAuthBindModalState()
}

function backToAuthBindForm() {
  authBindStepIndex.value = 0
  authBindResultMode.value = 'confirm'
  authBindResult.value = null
  authBindForm.pin = ''
}

async function refreshAuthBindCertList() {
  authBindCertLoading.value = true
  try {
    const options = await loadJitCertList()
    authBindCertOptions.value = options
    if (options.length > 0 && !options.some((item) => item.sn === authBindForm.certSn)) {
      authBindForm.certSn = options[0]?.sn ?? ''
    }
    if (!options.length) {
      msg.warning('未检测到可用数字证书，请确认已插入 USB Key')
    }
  } catch (e: unknown) {
    const err = e as { message?: string }
    msg.error(err?.message || '刷新证书列表失败')
  } finally {
    authBindCertLoading.value = false
  }
}

async function goAuthBindResultStep() {
  authBindStepIndex.value = 1
  authBindResultCollapseKeys.value = authBindHasExtraCertInfo.value ? ['more-cert'] : []
  await nextTick()
  authBindModalBodyRef.value?.scrollTo?.({ top: 0, behavior: 'smooth' })
}

async function onAuthBindSubmit() {
  if (!authBindForm.certSn) {
    msg.warning('请选择证书')
    return
  }
  if (!authBindForm.pin.trim()) {
    msg.warning('请输入 PIN 码')
    return
  }
  authBindSubmitting.value = true
  authBindResult.value = null
  try {
    const random = await fetchJitAuthRandom()
    if (random === '') {
      msg.error('随机数获取失败，请确认后端与网关配置正常')
      return
    }
    const signObj = await signJitP1WithPin(random, authBindForm.certSn, authBindForm.pin)
    const authResult = await submitJitP1Auth(
      random,
      typeof signObj === 'object' ? signObj.signdata : undefined,
      typeof signObj === 'object' ? signObj.cert : undefined,
      typeof signObj === 'object' ? signObj.algid : undefined,
    )
    if (authResult === '') {
      authBindResult.value = { success: false, errDesc: '认证接口无有效返回' }
    } else {
      authBindResult.value = authResult
    }
    authBindResultMode.value = 'confirm'
    await goAuthBindResultStep()
  } finally {
    authBindSubmitting.value = false
  }
}

async function onAuthBindConfirm() {
  const result = authBindResult.value
  if (!result?.success) return

  const selectedCert = authBindCertOptions.value.find((item) => item.sn === authBindForm.certSn)
  const certSn = pickJitCertSn(result, selectedCert?.sn || authBindForm.certSn)
  if (!certSn) {
    msg.warning('未获取到证书序列号，无法绑定')
    return
  }

  authBindBinding.value = true
  try {
    await bindJitAuthToAccount({
      certSn,
      certDn: result.certDn || selectedCert?.dn,
      certAttributes: result.certAttributes,
      umsAttributes: result.umsAttributes,
      pmsAttributes: result.pmsAttributes,
      customAttributes: result.customAttributes,
    })
    await loadAuthBindInfo()
    msg.success('认证信息已绑定到当前账号')
    authBindResultMode.value = 'view'
    await goAuthBindResultStep()
  } catch (e: unknown) {
    const err = e as { message?: string }
    msg.error(err?.message || '绑定失败')
  } finally {
    authBindBinding.value = false
  }
}

function openProfileEditModal() {
  const p = userProfileStore.profile
  if (!p) return
  profileForm.name = p.realName ?? ''
  profileForm.phone = p.phone ?? ''
  profileForm.password = ''
  profileForm.avatarUrl = p.avatarUrl ?? null
  profileEditModalOpen.value = true
  nextTick(() => profileFormRef.value?.clearValidate?.())
}

function onProfileEditCancel() {
  profileEditModalOpen.value = false
}

async function onProfileEditSave() {
  try {
    await profileFormRef.value?.validate?.()
  } catch {
    return
  }
  const p = userProfileStore.profile
  if (!p?.id) {
    msg.error('未获取到用户信息，请刷新后重试')
    return
  }
  profileSubmitting.value = true
  try {
    const nextName = profileForm.name.trim()
    const nextPhone = profileForm.phone.trim()
    await updateUserInfo({
      id: p.id,
      name: nextName,
      phone: nextPhone,
      password: profileForm.password,
    })
    // 先写入本地资料，保证弹窗/顶栏立即刷新；再拉取服务端最新数据
    userProfileStore.setProfile({
      ...p,
      realName: nextName,
      phone: nextPhone,
    })
    msg.success('保存成功')
    await userProfileStore.load()
    profileEditModalOpen.value = false
  } finally {
    profileSubmitting.value = false
  }
}

async function beforeAvatarUpload(file: File) {
  const isImage = file.type.startsWith('image/')
  if (!isImage) {
    msg.warning('请上传图片文件')
    return false
  }
  const maxMb = 2
  if (file.size > maxMb * 1024 * 1024) {
    msg.warning(`图片大小不能超过 ${maxMb}MB`)
    return false
  }
  const p = userProfileStore.profile
  if (!p?.id) {
    msg.error('未获取到用户信息，请刷新后重试')
    return false
  }
  avatarUploading.value = true
  try {
    const url = await updateAvatar(file)
    profileForm.avatarUrl = url || null
    msg.success('头像已更新')
    await userProfileStore.load()
  } catch {
    // 请求层已统一提示接口异常
  } finally {
    avatarUploading.value = false
  }
  // 阻止 Upload 组件继续走默认上传流程（我们已在此处完成上传）
  return false
}

function sendChangePasswordSmsCode() {
  const phone = (userProfileStore.profile?.phone ?? '').trim()
  if (!phone) {
    msg.warning('当前账号未绑定手机号，无法使用短信验证')
    return
  }
  if (!phoneRegex.test(phone)) {
    msg.warning('请输入正确的手机号')
    return
  }
  if (smsCountdown.value > 0) return
  changePasswordSmsPicModalOpen.value = true
  void fetchChangePasswordSmsModalPicCaptcha()
}

async function onChangePasswordOk() {
  const phone = (userProfileStore.profile?.phone ?? '').trim()
  const hasPhone = Boolean(phone && phoneRegex.test(phone))
  try {
    await changePasswordFormRef.value?.validate()
  } catch {
    return
  }
  changePasswordSubmitting.value = true
  try {
    if (hasPhone) {
      if (!phoneValidCodeReqNo.value) {
        msg.warning('请先获取短信验证码')
        return
      }
      await resetPasswordByPhone({
        phone,
        userMode: 100,
        validCode: changePasswordForm.smsCode.trim(),
        validCodeReqNo: phoneValidCodeReqNo.value,
        newPassword: changePasswordForm.newPassword,
      })
    } else {
      await updatePasswordByOldPassword({
        password: changePasswordForm.oldPassword,
        newPassword: changePasswordForm.newPassword,
      })
    }
    msg.success('密码修改成功，请重新登录')
    changePasswordModalOpen.value = false
    await authStore.logout()
    window.location.href = '/login'
  } catch {
    // 请求层已统一提示接口异常
  } finally {
    changePasswordSubmitting.value = false
  }
}

function onChangePasswordCancel() {
  changePasswordModalOpen.value = false
}

function goToDashboard() {
  const hasPerm = permissionStore.hasPerm
  const name = resolveDashboardScreenRouteName(
    hasLargeScreenOrgPerm(hasPerm),
    hasLargeScreenRegionPerm(hasPerm),
  )
  const href = router.resolve({ name }).href
  window.open(href, '_blank', 'noopener,noreferrer')
}

/** 仅刷新角标未读数（轻量） */
async function loadUnreadBadge() {
  await messageNoticeStore.refreshCounts()
}

/** 弹窗内：同时拉未读、已读列表 */
async function loadNotificationModalLists() {
  notificationLoading.value = true
  try {
    const [u, r] = await Promise.all([
      fetchNotificationList({ limit: INBOX_PREVIEW_LIMIT, read: false }),
      fetchNotificationList({ limit: INBOX_PREVIEW_LIMIT, read: true }),
      messageNoticeStore.refreshCounts(),
    ])
    notificationsUnread.value = u
    notificationsRead.value = r
  } catch {
    notificationsUnread.value = []
    notificationsRead.value = []
  } finally {
    notificationLoading.value = false
  }
}

async function loadNotifications() {
  await loadUnreadBadge()
}

watch(notificationModalOpen, (open) => {
  if (open) void loadNotificationModalLists()
})

function openNotifications() {
  notificationModalOpen.value = true
  notificationTabKey.value = 'unread'
}

async function onMarkAllNotificationsRead() {
  try {
    await markAllMessagesRead()
    msg.success('已全部标记为已读')
    await loadNotificationModalLists()
    window.dispatchEvent(new CustomEvent('sys:index-message-changed'))
  } catch {
    // request 已提示
  }
}

async function onOpenNotificationItem(item: NotificationItem) {
  if (!item?.id) return
  notificationDetailItem.value = item
  notificationDetailOpen.value = true
  try {
    await fetchNotificationDetail(item.id)
    notificationsUnread.value = notificationsUnread.value.filter((n) => n.id !== item.id)
    if (!notificationsRead.value.some((n) => n.id === item.id)) {
      notificationsRead.value = [item, ...notificationsRead.value]
    }
    await messageNoticeStore.refreshCounts()
    window.dispatchEvent(new CustomEvent('sys:index-message-changed'))
  } catch {
    // request 已提示
  }
}

function getNotificationLevelText(level: NotificationItem['level']) {
  return notificationLevelLabel(level)
}

function getNotificationLevelColor(level: NotificationItem['level']) {
  return notificationLevelTagColor(level)
}

async function logout() {
  userProfileStore.clear()
  await authStore.logout()
  window.location.href = '/login'
}
</script>

<template>
  <header class="app-header">
    <div class="header-bg-pattern" aria-hidden />
    <div class="header-content">
      <div class="header-left">
        <div class="header-logo">
          <img
            class="header-logo-image"
            :src="sysBranding.logoUrlOrDefault"
            alt=""
            referrerpolicy="no-referrer"
          />
        </div>
        <h1 class="header-title">{{ sysBranding.systemName }}</h1>
      </div>
      <p class="header-secrecy" role="note">
        <SafetyCertificateOutlined class="header-secrecy-icon" aria-hidden="true" />
        <span>涉密不上网，上网不涉密</span>
      </p>
      <div class="header-right">
        <a
          class="header-icon"
          href="javascript:void(0)"
          title="一张网数据大屏"
          @click="goToDashboard"
        >
          <BarChartOutlined />
        </a>
        <HeaderManualDownload />
        <a-badge :count="unreadNotificationCount" :offset="[-2, 2]" class="header-badge">
          <a class="header-icon" href="javascript:void(0)" title="通知" @click="openNotifications">
            <BellOutlined />
          </a>
        </a-badge>
        <a-dropdown v-if="authStore.isLoggedIn">
          <div class="header-user">
            <a-avatar
              :size="32"
              class="header-avatar"
              :src="userProfileStore.profile?.avatarUrl || undefined"
            >
              <template v-if="!userProfileStore.profile?.avatarUrl" #icon>
                <UserOutlined />
              </template>
            </a-avatar>
            <span class="header-username">{{ displayName }}</span>
            <DownOutlined class="header-user-arrow" />
          </div>
          <template #overlay>
            <a-menu>
              <a-menu-item key="profile" @click="openProfileModal">个人信息</a-menu-item>
              <a-menu-item key="change-password" @click="changePasswordModalOpen = true">
                修改密码
              </a-menu-item>
              <a-menu-item key="logout" @click="logout">退出登录</a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
        <div v-else class="header-user">
          <a-avatar :size="32" class="header-avatar">
            <template #icon><UserOutlined /></template>
          </a-avatar>
          <span class="header-username">未登录</span>
        </div>
      </div>
    </div>
    <a-modal
      v-model:open="profileModalOpen"
      title="个人信息"
      width="600px"
      :footer="null"
      destroy-on-close
      centered
      class="profile-modal"
      @cancel="onProfileCancel"
    >
      <a-spin :spinning="userProfileStore.loading">
        <div v-if="userProfileStore.profile" class="profile-panel">
          <div class="profile-hero">
            <a-avatar
              :size="64"
              :src="userProfileStore.profile.avatarUrl || undefined"
              class="profile-hero-avatar"
            >
              <template v-if="!userProfileStore.profile.avatarUrl" #icon>
                <UserOutlined />
              </template>
            </a-avatar>
            <div class="profile-hero-main">
              <div class="profile-hero-name">{{ userProfileStore.profile.realName || '—' }}</div>
              <div class="profile-hero-meta">
                <span class="profile-hero-username">{{ userProfileStore.profile.username }}</span>
                <a-tag
                  v-if="userProfileStore.profile.role"
                  color="processing"
                  class="profile-hero-role"
                >
                  {{ userProfileStore.profile.role }}
                </a-tag>
              </div>
            </div>
            <a-button
              type="primary"
              ghost
              size="small"
              class="profile-hero-edit"
              @click="openProfileEditModal"
            >
              <EditOutlined />
              编辑
            </a-button>
          </div>

          <div class="profile-info-card">
            <div class="profile-info-grid">
              <div class="profile-info-item">
                <span class="profile-info-label">手机号码</span>
                <span class="profile-info-value">{{ userProfileStore.profile.phone || '—' }}</span>
              </div>
              <div class="profile-info-item">
                <span class="profile-info-label">地区</span>
                <span class="profile-info-value">{{ userProfileStore.profile.region || '—' }}</span>
              </div>
              <div class="profile-info-item">
                <span class="profile-info-label">部门</span>
                <span class="profile-info-value">{{
                  userProfileStore.profile.department || '—'
                }}</span>
              </div>
              <div v-if="userProfileStore.profile.lastLoginAt" class="profile-info-item">
                <span class="profile-info-label">最近登录</span>
                <span class="profile-info-value profile-info-value-muted">
                  {{ userProfileStore.profile.lastLoginAt }}
                </span>
              </div>
            </div>
          </div>

          <div class="profile-auth-card">
            <div class="profile-auth-head">
              <SafetyCertificateOutlined class="profile-auth-icon" />
              <span class="profile-auth-title">认证授权绑定</span>
              <a-tag :color="authBindInfo?.bound ? 'success' : 'default'" class="profile-auth-tag">
                {{ authBindInfoLoading ? '加载中' : authBindInfo?.bound ? '已绑定' : '未绑定' }}
              </a-tag>
            </div>
            <div class="profile-auth-desc" :title="profileAuthBindFullText || undefined">
              {{ profileAuthBindShortText }}
            </div>
            <div class="profile-auth-actions">
              <a-button
                v-if="authBindInfo?.bound"
                type="link"
                size="small"
                class="profile-auth-action"
                @click="openAuthBindResultView"
              >
                查看认证信息
              </a-button>
              <a-button
                type="link"
                size="small"
                class="profile-auth-action"
                @click="openAuthBindModal"
              >
                {{ authBindInfo?.bound ? '重新认证' : '去绑定' }}
              </a-button>
            </div>
          </div>
        </div>
        <div v-else-if="!userProfileStore.loading" class="profile-modal-empty">暂无用户信息</div>
      </a-spin>
    </a-modal>

    <a-modal
      v-model:open="profileEditModalOpen"
      title="编辑个人信息"
      width="560px"
      ok-text="保存"
      cancel-text="取消"
      :confirm-loading="profileSubmitting"
      destroy-on-close
      centered
      @ok="onProfileEditSave"
      @cancel="onProfileEditCancel"
    >
      <a-form ref="profileFormRef" :model="profileForm" :rules="profileRules" layout="vertical">
        <div class="profile-avatar-row">
          <a-avatar :size="72" :src="profileForm.avatarUrl || undefined" class="profile-avatar">
            <template v-if="!profileForm.avatarUrl" #icon>
              <UserOutlined />
            </template>
          </a-avatar>
          <a-upload :show-upload-list="false" :before-upload="beforeAvatarUpload" accept="image/*">
            <a-button :loading="avatarUploading">更换头像</a-button>
          </a-upload>
        </div>
        <a-form-item label="姓名" name="name">
          <a-input
            v-model:value="profileForm.name"
            placeholder="请输入姓名"
            :maxlength="PERSON_NAME_LIMITS.max"
            show-count
          />
        </a-form-item>
        <a-form-item label="手机号码" name="phone">
          <a-input
            v-model:value="profileForm.phone"
            placeholder="请输入手机号码"
            :maxlength="PHONE_LIMITS.cnLength"
          />
        </a-form-item>
        <a-form-item label="登录密码" name="password">
          <a-input-password
            v-model:value="profileForm.password"
            placeholder="请输入当前登录密码以确认修改"
            autocomplete="current-password"
            :maxlength="32"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      v-model:open="authBindModalOpen"
      title="认证授权绑定"
      width="680px"
      :footer="null"
      destroy-on-close
      centered
      class="auth-bind-modal"
      @cancel="onAuthBindCancel"
    >
      <div ref="authBindModalBodyRef" class="auth-bind-modal-body">
        <a-steps
          :current="authBindStepIndex"
          size="small"
          class="auth-bind-steps"
          :items="authBindStepItems"
        />

        <a-spin :spinning="authBindCertLoading">
          <!-- 步骤 1：证书认证 -->
          <section v-show="authBindStepIndex === 0" class="auth-bind-step-panel">
            <a-alert
              type="info"
              show-icon
              class="auth-bind-tip-alert"
              message="请先完成网关认证"
              description="选择证书并输入 PIN 码，完成后进入下一步查看认证结果。"
            />

            <div class="auth-bind-form-card">
              <div class="auth-bind-form-card-head">
                <SafetyCertificateOutlined class="auth-bind-form-card-icon" />
                <span>证书与 PIN</span>
              </div>
              <a-form layout="vertical" class="auth-bind-form">
                <a-row :gutter="16">
                  <a-col :span="14">
                    <a-form-item label="证书项" required>
                      <a-select
                        v-model:value="authBindForm.certSn"
                        placeholder="请选择证书"
                        :options="
                          authBindCertOptions.map((item) => ({
                            value: item.sn,
                            label: item.dn,
                          }))
                        "
                        show-search
                        option-filter-prop="label"
                      />
                    </a-form-item>
                  </a-col>
                  <a-col :span="10">
                    <a-form-item label="PIN码" required>
                      <a-input-password
                        v-model:value="authBindForm.pin"
                        placeholder="请输入 PIN 码"
                        autocomplete="off"
                      />
                    </a-form-item>
                  </a-col>
                </a-row>
                <a-button
                  type="link"
                  :loading="authBindCertLoading"
                  class="auth-bind-refresh-link"
                  @click="refreshAuthBindCertList"
                >
                  <ReloadOutlined />
                  刷新证书列表
                </a-button>
              </a-form>
            </div>
          </section>

          <!-- 步骤 2：认证结果 -->
          <section v-show="authBindStepIndex === 1" class="auth-bind-step-panel">
            <template v-if="authBindResult?.success">
              <div
                class="auth-bind-result-banner"
                :class="authBindResultMode === 'confirm' ? 'is-success' : 'is-info'"
              >
                <CheckCircleFilled class="auth-bind-result-banner-icon" />
                <div class="auth-bind-result-banner-text">
                  <div class="auth-bind-result-banner-title">
                    {{ authBindResultMode === 'confirm' ? '认证成功' : '已绑定认证信息' }}
                  </div>
                  <div class="auth-bind-result-banner-desc">
                    {{
                      authBindResultMode === 'confirm'
                        ? '请核对下方信息，确认无误后绑定到当前账号'
                        : '以下为已保存的认证绑定信息'
                    }}
                  </div>
                </div>
                <a-tag color="success">成功</a-tag>
              </div>

              <div class="auth-bind-result-card">
                <div class="auth-bind-result-block">
                  <div class="auth-bind-result-block-title">当前账号</div>
                  <div class="auth-bind-kv-grid">
                    <span class="auth-bind-kv-label">用户名</span>
                    <span class="auth-bind-kv-value">
                      {{ userProfileStore.profile?.username || '—' }}
                    </span>
                    <span class="auth-bind-kv-label">姓名</span>
                    <span class="auth-bind-kv-value">
                      {{ userProfileStore.profile?.realName || displayName }}
                    </span>
                    <template v-if="authBindInfo?.bound && authBindResultMode === 'view'">
                      <span class="auth-bind-kv-label">绑定时间</span>
                      <span class="auth-bind-kv-value">
                        {{ formatJitAuthBindTime(authBindInfo.bindTime) || '—' }}
                      </span>
                    </template>
                  </div>
                </div>

                <div class="auth-bind-result-block">
                  <div class="auth-bind-result-block-title">证书摘要</div>
                  <div class="auth-bind-kv-grid auth-bind-kv-grid-single">
                    <span class="auth-bind-kv-label">证书主体</span>
                    <span class="auth-bind-kv-value" :title="authBindCertSummary.subjectFull">
                      {{ authBindCertSummary.subject }}
                    </span>
                    <span class="auth-bind-kv-label">序列号</span>
                    <span class="auth-bind-kv-value auth-bind-kv-mono">
                      {{ authBindCertSummary.serial }}
                    </span>
                    <template v-if="authBindCertSummary.notBefore">
                      <span class="auth-bind-kv-label">有效期起</span>
                      <span class="auth-bind-kv-value">{{ authBindCertSummary.notBefore }}</span>
                    </template>
                    <template v-if="authBindCertSummary.notAfter">
                      <span class="auth-bind-kv-label">有效期止</span>
                      <span class="auth-bind-kv-value">{{ authBindCertSummary.notAfter }}</span>
                    </template>
                  </div>
                </div>

                <div v-if="authBindHasUmsInfo" class="auth-bind-result-block">
                  <div class="auth-bind-result-block-title">身份信息</div>
                  <div class="auth-bind-kv-grid">
                    <template v-for="item in authBindUmsEntries" :key="item.key">
                      <span class="auth-bind-kv-label">{{ item.label }}</span>
                      <span class="auth-bind-kv-value">{{ item.value }}</span>
                    </template>
                  </div>
                </div>
              </div>

              <a-collapse
                v-if="authBindHasExtraCertInfo"
                v-model:active-key="authBindResultCollapseKeys"
                :bordered="false"
                class="auth-bind-result-collapse"
              >
                <a-collapse-panel key="more-cert" header="更多证书字段">
                  <div class="auth-bind-kv-grid auth-bind-kv-grid-single auth-bind-kv-grid-compact">
                    <template v-for="item in authBindExtraCertEntries" :key="item.key">
                      <span class="auth-bind-kv-label">{{ item.label }}</span>
                      <span class="auth-bind-kv-value auth-bind-kv-break">{{ item.value }}</span>
                    </template>
                  </div>
                </a-collapse-panel>
              </a-collapse>

              <a-empty
                v-if="!authBindHasCertInfo && !authBindHasUmsInfo"
                description="未返回证书或用户信息"
              />
            </template>

            <template v-else>
              <div class="auth-bind-result-banner is-error">
                <CloseCircleFilled class="auth-bind-result-banner-icon" />
                <div class="auth-bind-result-banner-text">
                  <div class="auth-bind-result-banner-title">认证失败</div>
                  <div class="auth-bind-result-banner-desc">
                    {{ authBindResult?.errDesc || '请检查证书与 PIN 码后重试' }}
                  </div>
                </div>
                <a-tag color="error">失败</a-tag>
              </div>

              <div
                v-if="authBindResult?.errCode"
                class="auth-bind-result-card auth-bind-result-card-error"
              >
                <div class="auth-bind-kv-grid auth-bind-kv-grid-single">
                  <span class="auth-bind-kv-label">错误码</span>
                  <span class="auth-bind-kv-value auth-bind-kv-mono">{{
                    authBindResult.errCode
                  }}</span>
                </div>
              </div>
            </template>
          </section>
        </a-spin>
      </div>

      <div class="auth-bind-modal-footer">
        <a-button @click="onAuthBindCancel">取消</a-button>
        <template v-if="authBindStepIndex === 0">
          <a-button type="primary" :loading="authBindSubmitting" @click="onAuthBindSubmit">
            认证
          </a-button>
        </template>
        <template v-else-if="authBindResultMode === 'view'">
          <a-button type="primary" @click="closeAuthBindResult">关闭</a-button>
        </template>
        <template v-else>
          <a-button @click="backToAuthBindForm">
            <ReloadOutlined />
            重新认证
          </a-button>
          <a-button
            type="primary"
            :loading="authBindBinding"
            :disabled="!authBindResult?.success"
            @click="onAuthBindConfirm"
          >
            确认绑定到当前账号
          </a-button>
        </template>
      </div>
    </a-modal>

    <a-modal
      v-model:open="passwordExpireRemindOpen"
      :title="
        passwordExpireView.expired
          ? '密码已过期'
          : passwordExpireView.tone === 'danger'
            ? '请修改密码'
            : '密码即将过期'
      "
      width="440px"
      :mask-closable="false"
      centered
      wrap-class-name="pwd-expire-remind-modal-wrap"
      @cancel="dismissPasswordExpireRemind"
    >
      <div class="pwd-expire-remind-body">
        <ExclamationCircleFilled
          class="pwd-expire-remind-icon"
          :class="passwordExpireView.tone === 'danger' ? 'is-danger' : 'is-warn'"
        />
        <p class="pwd-expire-remind-text">
          {{ passwordExpireView.statusText }}。最近
          {{ PASSWORD_REMIND_LAST_DAYS }} 天内每次登录都会提醒修改密码。
        </p>
        <div class="pwd-expire-remind-meta">
          <span>过期时间</span>
          <strong>{{ passwordExpireView.expireText }}</strong>
        </div>
      </div>
      <template #footer>
        <a-button @click="dismissPasswordExpireRemind">稍后提醒</a-button>
        <a-button type="primary" @click="goChangePasswordFromRemind">立即修改</a-button>
      </template>
    </a-modal>

    <a-modal
      v-model:open="changePasswordModalOpen"
      width="520px"
      ok-text="确认修改"
      cancel-text="取消"
      destroy-on-close
      :confirm-loading="changePasswordSubmitting"
      centered
      wrap-class-name="change-password-modal-wrap"
      @ok="onChangePasswordOk"
      @cancel="onChangePasswordCancel"
    >
      <template #title>
        <div class="change-password-title">
          <span class="change-password-title-icon"><LockOutlined /></span>
          <div class="change-password-title-text">
            <span>修改密码</span>
            <small
              >有效期 {{ PASSWORD_VALID_DAYS }} 天，最近
              {{ PASSWORD_REMIND_LAST_DAYS }} 天每次登录将提醒修改</small
            >
          </div>
        </div>
      </template>
      <div class="change-password-body">
        <div class="pwd-expire-card" :class="`is-${passwordExpireView.tone}`">
          <div class="pwd-expire-card-head">
            <span class="pwd-expire-dot" />
            <strong>{{ passwordExpireView.statusText }}</strong>
          </div>
          <div class="pwd-expire-card-meta">
            <div>
              <em>{{ passwordExpireView.updateLabel }}</em>
              <b>{{ passwordExpireView.updateText }}</b>
            </div>
            <div>
              <em>过期时间</em>
              <b>{{ passwordExpireView.expireText }}</b>
            </div>
          </div>
          <p class="pwd-expire-card-hint">
            {{
              changePasswordHasPhone
                ? '已绑定手机号，通过短信验证修改，无需输入原密码'
                : '未绑定手机号，需输入原密码后修改，建议先绑定手机号'
            }}
          </p>
        </div>
        <a-form
          ref="changePasswordFormRef"
          :model="changePasswordForm"
          :rules="changePasswordRules"
          layout="vertical"
          class="change-password-form"
          :required-mark="true"
        >
          <div class="change-password-grid">
            <a-form-item label="当前账号" :required="false">
              <a-input :value="userProfileStore.profile?.username || ''" disabled>
                <template #prefix><UserOutlined /></template>
              </a-input>
            </a-form-item>
            <a-form-item v-if="changePasswordHasPhone" label="已绑定手机号" :required="false">
              <a-input :value="userProfileStore.profile?.phone" disabled>
                <template #prefix><SafetyCertificateOutlined /></template>
              </a-input>
            </a-form-item>
          </div>
          <a-form-item
            v-if="!changePasswordHasPhone"
            label="原密码"
            name="oldPassword"
            :required="true"
          >
            <a-input-password
              v-model:value="changePasswordForm.oldPassword"
              placeholder="请输入当前登录密码"
              :maxlength="32"
              autocomplete="current-password"
            />
          </a-form-item>
          <a-form-item
            v-if="changePasswordHasPhone"
            label="短信验证码"
            name="smsCode"
            :required="true"
          >
            <div class="change-password-sms-row">
              <a-input
                v-model:value="changePasswordForm.smsCode"
                placeholder="请输入 6 位验证码"
                :maxlength="PHONE_LIMITS.otpLength"
                autocomplete="one-time-code"
              />
              <a-button
                html-type="button"
                :type="smsCountdown > 0 ? 'default' : 'primary'"
                :disabled="smsCountdown > 0"
                @click="sendChangePasswordSmsCode"
              >
                {{ smsCountdown > 0 ? `${smsCountdown}s` : '获取验证码' }}
              </a-button>
            </div>
          </a-form-item>
          <a-form-item label="新密码" name="newPassword" :required="true">
            <a-input-password
              v-model:value="changePasswordForm.newPassword"
              placeholder="8–32 位，含大小写、数字和特殊字符"
              :maxlength="32"
              autocomplete="new-password"
            />
            <div class="change-password-hints">
              <span
                v-for="rule in passwordRuleChecks"
                :key="rule.key"
                :class="{ 'is-ok': rule.ok }"
              >
                <CheckCircleFilled v-if="rule.ok" />
                {{ rule.label }}
              </span>
            </div>
          </a-form-item>
          <a-form-item label="确认新密码" name="confirmPassword" :required="true">
            <a-input-password
              v-model:value="changePasswordForm.confirmPassword"
              placeholder="请再次输入新密码"
              :maxlength="32"
              autocomplete="new-password"
            />
            <p v-if="confirmPasswordMatched" class="change-password-match">
              <CheckCircleFilled /> 两次密码一致
            </p>
          </a-form-item>
        </a-form>
      </div>
    </a-modal>
    <!-- 修改密码：获取短信前图形验证（与登录页短信发送一致） -->
    <a-modal
      v-model:open="changePasswordSmsPicModalOpen"
      title="图形验证"
      :width="400"
      :confirm-loading="changePasswordSmsSendSubmitting"
      ok-text="发送验证码"
      cancel-text="取消"
      destroy-on-close
      centered
      @ok="confirmSendChangePasswordSmsCode"
      @cancel="closeChangePasswordSmsPicModal"
    >
      <p class="change-password-sms-pic-tip">请先输入图中验证码，再获取手机验证码</p>
      <div class="change-password-captcha-row">
        <div
          class="change-password-captcha-box"
          title="点击刷新"
          @click="refreshChangePasswordSmsModalPicCaptcha"
        >
          <img
            v-if="changePasswordSmsPicCaptchaImgSrc && !changePasswordSmsPicCaptchaLoading"
            :src="changePasswordSmsPicCaptchaImgSrc"
            alt=""
            class="change-password-captcha-img"
            width="120"
            height="40"
            draggable="false"
          />
          <div v-else class="change-password-captcha-placeholder">
            {{ changePasswordSmsPicCaptchaLoading ? '加载中…' : '点击获取' }}
          </div>
        </div>
        <a-input
          v-model:value="changePasswordSmsPicCaptchaInput"
          placeholder="请输入图中验证码"
          :maxlength="PHONE_LIMITS.picCaptchaMaxLength"
          autocomplete="off"
          class="change-password-captcha-input"
        />
        <a-button
          type="link"
          class="change-password-captcha-refresh"
          @click="refreshChangePasswordSmsModalPicCaptcha"
        >
          换一张
        </a-button>
      </div>
    </a-modal>
    <a-modal
      v-model:open="notificationModalOpen"
      title="消息通知"
      width="620px"
      :footer="null"
      centered
      wrap-class-name="notification-message-modal-wrap"
      class="notification-message-modal"
    >
      <div class="notification-modal-inner">
        <a-tabs v-model:activeKey="notificationTabKey" class="notification-tabs">
          <a-tab-pane key="unread" :tab="`未读 (${unreadNotificationCount})`"></a-tab-pane>
          <a-tab-pane key="read" :tab="`已读 (${readNotificationCount})`"></a-tab-pane>
        </a-tabs>
        <div class="notification-modal-body-scroll">
          <a-list
            :data-source="modalNotifications"
            :loading="notificationLoading"
            item-layout="vertical"
            class="notification-list"
            :locale="{ emptyText: notificationTabKey === 'read' ? '暂无已读消息' : '暂无未读消息' }"
          >
            <template #renderItem="{ item }">
              <a-list-item
                :key="item.id"
                class="notification-list-item"
                @click="onOpenNotificationItem(item)"
              >
                <a-list-item-meta :description="item.time">
                  <template #title>
                    <div class="notification-title-row">
                      <span>{{ item.title }}</span>
                      <a-tag
                        :color="getNotificationLevelColor(item.level)"
                        class="notification-level-tag"
                      >
                        {{ getNotificationLevelText(item.level) }}
                      </a-tag>
                    </div>
                  </template>
                </a-list-item-meta>
                <div class="notification-content">{{ item.content }}</div>
              </a-list-item>
            </template>
          </a-list>
          <p v-if="inboxPreviewHint" class="notification-preview-hint">{{ inboxPreviewHint }}</p>
        </div>
        <div class="notification-modal-actions">
          <a-button type="link" class="notification-mark-all" @click="onMarkAllNotificationsRead">
            全部标记已读
          </a-button>
        </div>
      </div>
    </a-modal>
    <a-modal
      v-model:open="notificationDetailOpen"
      :title="notificationDetailItem?.title || '消息详情'"
      width="520px"
      :footer="null"
      :z-index="1100"
      centered
      destroy-on-close
      wrap-class-name="notification-detail-modal-wrap"
    >
      <div v-if="notificationDetailItem" class="notification-detail">
        <div class="notification-detail__meta">
          <a-tag :color="getNotificationLevelColor(notificationDetailItem.level)">
            {{ getNotificationLevelText(notificationDetailItem.level) }}
          </a-tag>
          <time v-if="notificationDetailItem.time" :datetime="notificationDetailItem.time">
            {{ notificationDetailItem.time }}
          </time>
        </div>
        <div class="notification-detail__body">
          {{ notificationDetailItem.content || '暂无详情' }}
        </div>
      </div>
    </a-modal>
  </header>
</template>

<style scoped>
.change-password-form :deep(.ant-form-item-label > label.ant-form-item-required::before) {
  color: #ff4d4f !important;
}

.app-header {
  position: relative;
  display: flex;
  align-items: center;
  height: 64px;
  flex-shrink: 0;
  background: var(--nav-header-bg);
  overflow: hidden;
  font-family: var(--nav-font-ui);
  border-bottom: 1px solid var(--nav-header-border);
  box-shadow:
    0 1px 0 rgba(0, 0, 0, 0.12),
    0 4px 16px rgba(15, 23, 42, 0.18);
}

.header-bg-pattern {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 24px 24px;
  pointer-events: none;
  opacity: 0.8;
}

.header-bg-pattern::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 80% at 10% 50%,
    rgba(56, 189, 248, 0.08) 0%,
    transparent 50%
  );
  pointer-events: none;
}

.header-content {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  width: 100%;
  padding: 0 28px;
  column-gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.header-secrecy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid rgba(251, 191, 36, 0.38);
  background: linear-gradient(180deg, rgba(254, 243, 199, 0.18) 0%, rgba(245, 158, 11, 0.1) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
  color: #fde68a;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.header-secrecy-icon {
  flex-shrink: 0;
  font-size: 14px;
  color: #fbbf24;
}

.header-logo {
  flex-shrink: 0;
}

.header-logo-image {
  width: 42px;
  height: 42px;
  display: block;
  object-fit: contain;
}

.header-title {
  margin: 0;
  font-family: var(--nav-font-title);
  font-size: 22px;
  font-weight: 600;
  color: var(--nav-header-text);
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--nav-header-text-muted);
  font-size: 17px;
  border-radius: 10px;
  transition:
    color 0.2s ease,
    background 0.2s ease;
  cursor: pointer;
}

.header-icon:hover {
  color: var(--nav-header-text);
  background: var(--nav-header-hover-bg);
}

.header-badge :deep(.ant-badge) {
  line-height: 1;
}

.header-badge :deep(.ant-badge-count) {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 5px !important;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  line-height: 1 !important;
  border: 1px solid rgba(10, 22, 40, 0.6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 数字在徽标内垂直/水平居中（覆盖 ScrollNumber 默认行高） */
.header-badge :deep(.ant-scroll-number-only),
.header-badge :deep(.ant-scroll-number-only-unit) {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  height: 100%;
  line-height: 1 !important;
}

.notification-list :deep(.ant-list-item-meta-title) {
  margin-bottom: 4px;
  font-size: 15px;
  font-weight: 600;
}

.notification-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.notification-level-tag {
  margin-inline-end: 0;
}

.notification-list-item {
  cursor: pointer;
}

.notification-list :deep(.ant-list-item-meta-description) {
  font-size: 12px;
  color: #8c8c8c;
}

.notification-content {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.6;
  color: #595959;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-detail__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #8c8c8c;
}

.notification-detail__body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.88);
}

.notification-message-modal :deep(.notification-tabs .ant-tabs-nav) {
  margin-bottom: 8px;
}

.notification-tabs {
  flex-shrink: 0;
}

.notification-modal-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.notification-modal-body-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.notification-preview-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.notification-modal-actions {
  flex-shrink: 0;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  text-align: right;
}
.notification-mark-all {
  padding: 0;
  height: auto;
}

.notification-list :deep(.ant-list-empty-text) {
  padding: 24px 0;
  text-align: center;
  color: #8c8c8c;
}

.change-password-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.change-password-title-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.2;
}

.change-password-title-text span {
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.change-password-title-text small {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
}

.change-password-title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: #fff;
  background: linear-gradient(160deg, var(--app-primary, #0369a1), #0ea5e9);
  box-shadow: 0 6px 14px rgba(3, 105, 161, 0.22);
}

.change-password-body {
  padding-top: 4px;
}

.pwd-expire-card {
  margin-bottom: 18px;
  padding: 12px 14px 10px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
  box-shadow: inset 3px 0 0 var(--app-primary, #0369a1);
}

.pwd-expire-card.is-warn {
  border-color: #fde68a;
  background: #fffbeb;
  box-shadow: inset 3px 0 0 #d97706;
}

.pwd-expire-card.is-danger {
  border-color: #fecaca;
  background: #fff7f6;
  box-shadow: inset 3px 0 0 #e11d48;
}

.pwd-expire-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pwd-expire-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--app-primary, #0369a1);
}

.pwd-expire-card.is-warn .pwd-expire-dot {
  background: #d97706;
}

.pwd-expire-card.is-danger .pwd-expire-dot {
  background: #e11d48;
}

.pwd-expire-card-head strong {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.pwd-expire-card-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 10px;
}

.pwd-expire-card-meta div {
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.pwd-expire-card-meta em {
  display: block;
  font-style: normal;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 2px;
}

.pwd-expire-card-meta b {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
}

.pwd-expire-card-hint {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.5);
}

.change-password-form :deep(.ant-form-item) {
  margin-bottom: 14px;
}

.change-password-form :deep(.ant-form-item-label) {
  padding-bottom: 4px;
}

.change-password-form :deep(.ant-form-item-label > label) {
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}

.change-password-form :deep(.ant-input-affix-wrapper),
.change-password-form :deep(.ant-input) {
  border-radius: 8px;
}

.change-password-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
}

.change-password-grid:has(> :only-child) {
  grid-template-columns: 1fr;
}

.change-password-hints {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.change-password-hints span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  background: #f5f5f5;
  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.change-password-hints span.is-ok {
  color: #15803d;
  background: #dcfce7;
}

.change-password-match {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0;
  font-size: 12px;
  color: #15803d;
}

.change-password-sms-row :deep(.ant-btn) {
  min-width: 104px;
  border-radius: 8px;
}

.pwd-expire-remind-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pwd-expire-remind-icon {
  font-size: 28px;
  margin-bottom: 10px;
}

.pwd-expire-remind-icon.is-warn {
  color: #d48806;
}

.pwd-expire-remind-icon.is-danger {
  color: #cf1322;
}

.pwd-expire-remind-text {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.75);
}

.pwd-expire-remind-meta {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fafafa;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}

.pwd-expire-remind-meta strong {
  color: rgba(0, 0, 0, 0.85);
  font-weight: 600;
}

.change-password-sms-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.change-password-sms-row :deep(.ant-input) {
  flex: 1;
  min-width: 0;
}

.change-password-sms-pic-tip {
  margin: 0 0 16px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
}

.change-password-captcha-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
}

.change-password-captcha-box {
  flex-shrink: 0;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f7fa;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.change-password-captcha-box:hover {
  border-color: var(--app-primary, #1677ff);
}

.change-password-captcha-img {
  display: block;
  width: 120px;
  height: 40px;
  object-fit: contain;
  vertical-align: top;
}

.change-password-captcha-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 40px;
  font-size: 12px;
  color: #8c8c8c;
  user-select: none;
}

.change-password-captcha-input {
  flex: 1;
  min-width: 120px;
}

.change-password-captcha-refresh {
  flex-shrink: 0;
  padding: 0 4px;
  height: auto;
  font-size: 13px;
}

.profile-avatar-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.profile-avatar {
  background: var(--app-primary);
}

.profile-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(22, 119, 255, 0.08) 0%, rgba(22, 119, 255, 0.02) 100%);
  border: 1px solid rgba(22, 119, 255, 0.12);
}

.profile-hero-avatar {
  flex-shrink: 0;
  background: var(--app-primary);
  box-shadow: 0 2px 8px rgba(22, 119, 255, 0.2);
}

.profile-hero-main {
  flex: 1;
  min-width: 0;
}

.profile-hero-name {
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.3;
}

.profile-hero-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.profile-hero-username {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}

.profile-hero-role {
  margin: 0;
  font-size: 12px;
  line-height: 20px;
}

.profile-hero-edit {
  flex-shrink: 0;
}

.profile-info-card {
  padding: 12px 14px;
  border: 1px solid var(--app-border, #f0f0f0);
  border-radius: 8px;
  background: #fff;
}

.profile-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
}

.profile-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.profile-info-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.4;
}

.profile-info-value {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.5;
  word-break: break-all;
}

.profile-info-value-muted {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}

.profile-auth-card {
  padding: 12px 14px;
  border: 1px solid var(--app-border, #f0f0f0);
  border-radius: 8px;
  background: #fafafa;
}

.profile-auth-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.profile-auth-icon {
  color: var(--app-primary);
  font-size: 15px;
}

.profile-auth-title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.profile-auth-tag {
  margin: 0;
  font-size: 12px;
  line-height: 20px;
}

.profile-auth-desc {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.5;
  word-break: break-all;
}

.profile-auth-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 8px;
}

.profile-auth-action {
  padding: 0;
  height: auto;
  font-size: 13px;
}

.auth-bind-form {
  margin-bottom: 0;
}

.auth-bind-modal-body {
  max-height: min(62vh, 480px);
  overflow-y: auto;
  padding-right: 4px;
}

.auth-bind-steps {
  margin-bottom: 16px;
}

.auth-bind-step-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.auth-bind-tip-alert {
  margin-bottom: 0;
}

.auth-bind-tip-alert :deep(.ant-alert-message) {
  font-size: 13px;
}

.auth-bind-tip-alert :deep(.ant-alert-description) {
  font-size: 12px;
}

.auth-bind-form-card {
  padding: 14px 16px;
  border: 1px solid var(--app-border, #f0f0f0);
  border-radius: 8px;
  background: #fff;
}

.auth-bind-form-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.auth-bind-form-card-icon {
  color: var(--app-primary);
  font-size: 15px;
}

.auth-bind-form :deep(.ant-form-item) {
  margin-bottom: 12px;
}

.auth-bind-refresh-link {
  padding-left: 0;
  height: auto;
  font-size: 13px;
}

.auth-bind-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.auth-bind-result-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
}

.auth-bind-result-banner.is-success {
  background: #f6ffed;
  border-color: #b7eb8f;
}

.auth-bind-result-banner.is-info {
  background: #e6f4ff;
  border-color: #91caff;
}

.auth-bind-result-banner.is-error {
  background: #fff2f0;
  border-color: #ffccc7;
}

.auth-bind-result-banner-icon {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 18px;
}

.auth-bind-result-banner.is-success .auth-bind-result-banner-icon {
  color: #52c41a;
}

.auth-bind-result-banner.is-info .auth-bind-result-banner-icon {
  color: #1677ff;
}

.auth-bind-result-banner.is-error .auth-bind-result-banner-icon {
  color: #ff4d4f;
}

.auth-bind-result-banner-text {
  flex: 1;
  min-width: 0;
}

.auth-bind-result-banner-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.4;
}

.auth-bind-result-banner-desc {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.5;
}

.auth-bind-result-card {
  border: 1px solid var(--app-border, #f0f0f0);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.auth-bind-result-card-error {
  padding: 10px 12px;
}

.auth-bind-result-block + .auth-bind-result-block {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.auth-bind-result-block {
  padding: 10px 12px;
}

.auth-bind-result-block-title {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  letter-spacing: 0.02em;
}

.auth-bind-kv-grid {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) 72px minmax(0, 1fr);
  gap: 6px 12px;
  align-items: start;
}

.auth-bind-kv-grid-single {
  grid-template-columns: 72px minmax(0, 1fr);
}

.auth-bind-kv-grid-compact {
  gap: 4px 10px;
}

.auth-bind-kv-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
}

.auth-bind-kv-value {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.5;
}

.auth-bind-kv-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.auth-bind-kv-break {
  word-break: break-all;
}

.auth-bind-result-collapse {
  background: transparent;
}

.auth-bind-result-collapse :deep(.ant-collapse-item) {
  border: 1px solid var(--app-border, #f0f0f0);
  border-radius: 8px !important;
  overflow: hidden;
  background: #fafafa;
}

.auth-bind-result-collapse :deep(.ant-collapse-header) {
  padding: 8px 12px !important;
  font-size: 13px;
  font-weight: 500;
}

.auth-bind-result-collapse :deep(.ant-collapse-content-box) {
  padding: 8px 12px 10px !important;
}

.profile-modal-empty {
  padding: 24px 0;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
}

.header-user {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 16px;
  padding: 6px 12px 6px 18px;
  border-left: 1px solid var(--nav-header-border);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.25s ease;
}

.header-user:hover {
  background: var(--nav-header-hover-bg);
}

.header-avatar {
  background: var(--app-primary);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22);
}

.header-username {
  font-size: 14px;
  font-weight: 500;
  color: var(--nav-header-text);
  letter-spacing: 0.02em;
}

.header-user-arrow {
  font-size: 11px;
  color: var(--nav-header-text-muted);
  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.header-user:hover .header-user-arrow {
  color: var(--nav-header-text);
}

@media (max-width: 991px) {
  .header-content {
    padding: 0 16px;
    column-gap: 10px;
  }

  .header-title {
    font-size: 17px;
  }

  .header-left {
    gap: 12px;
  }

  .header-secrecy {
    padding: 4px 10px;
    font-size: 12px;
    letter-spacing: 0.04em;
  }
}

@media (max-width: 767px) {
  .app-header {
    height: 56px;
  }

  .header-logo-image {
    width: 36px;
    height: 36px;
  }

  .header-title {
    font-size: 15px;
    letter-spacing: 0.03em;
  }

  .header-secrecy {
    padding: 3px 8px;
    gap: 4px;
  }

  .header-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .header-user {
    margin-left: 8px;
    padding: 4px 8px 4px 10px;
    gap: 8px;
  }
}

@media (max-width: 479px) {
  .header-title {
    display: none;
  }

  .header-username,
  .header-user-arrow {
    display: none;
  }

  .header-user {
    border-left: none;
    margin-left: 4px;
    padding: 4px;
  }

  .header-right {
    gap: 0;
  }
}
</style>

<!-- Modal 默认挂载到 body，wrap 与 content 高度需全局样式才能限制在视口内并启用内部滚动 -->
<style>
.notification-message-modal-wrap .ant-modal {
  max-width: calc(100vw - 24px);
}

.notification-message-modal-wrap .ant-modal-content {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
}

.notification-message-modal-wrap .ant-modal-header {
  flex-shrink: 0;
}

.notification-message-modal-wrap .ant-modal-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.change-password-modal-wrap .ant-modal-content {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
}

.change-password-modal-wrap .ant-modal-header {
  margin-bottom: 0;
  padding: 18px 24px 10px;
  border-bottom: none;
}

.change-password-modal-wrap .ant-modal-body {
  padding: 4px 24px 8px;
}

.change-password-modal-wrap .ant-modal-footer {
  padding: 8px 24px 18px;
  border-top: none;
}

.change-password-modal-wrap .ant-modal-footer .ant-btn {
  min-width: 88px;
  border-radius: 8px;
}
</style>
